// src/pages/fleet/master/VendorMasterPage.tsx — Vendor / dealer master.
//
// The list runs to lakhs of rows, so searching and paging happen on the server
// and the browser only ever holds one page. The whole list is loaded from the
// company's Excel sheet, which is checked before anything is saved.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getVendors, importVendors, saveVendor, vendorTemplate } from '../../../services/fleetApi';
import type { Vendor, VendorImportResult } from '../../../services/fleetApi';
import { P, css } from '../fleetStyles';
import { Modal, ReportShell, Tag } from '../reports/reportKit';
import { apiError } from '../../../utils/errors';
import { useAuth } from '../../../store/AuthContext';
import { can } from '../permissions';
import useLgd, { districtOptions, stateOptions } from './useLgd';

const TYPES = ['RETAILER', 'WHOLESALER', 'RAKEPOINT'];
const TYPE_TONE: Record<string, 'blue' | 'purple' | 'gray'> = { RETAILER: 'blue', WHOLESALER: 'purple', RAKEPOINT: 'gray' };
const PAGE_SIZE = 25;

// How much the dealer's map location can be trusted (set by npm run vendors:verify).
const GEO: Record<string, { label: string; tone: 'green' | 'blue' | 'orange' | 'red' | 'gray' }> = {
  OK: { label: 'Located', tone: 'green' },
  FIXED: { label: 'Corrected', tone: 'blue' },
  APPROX: { label: 'District only', tone: 'orange' },
  SUSPECT: { label: 'Looks wrong', tone: 'red' },
  MISSING: { label: 'No location', tone: 'gray' },
  UNRESOLVED: { label: 'Not placed', tone: 'red' },
};

export default function VendorMasterPage() {
  const { user } = useAuth();
  const canImport = can(user, 'master');
  const [rows, setRows] = useState<Vendor[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [geo, setGeo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const seq = useRef(0);

  const load = useCallback(() => {
    const mine = ++seq.current;
    setLoading(true);
    getVendors({ q: q || undefined, type: type || undefined, geo: geo || undefined, page, limit: PAGE_SIZE })
      .then((r) => {
        if (mine !== seq.current) return;               // an older search finished late
        setRows(r.data.rows); setTotal(r.data.total); setPages(r.data.pages); setError('');
      })
      .catch((e) => { if (mine === seq.current) { setRows([]); setError(apiError(e, 'Could not load vendors')); } })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  }, [q, type, geo, page]);

  // Search as you type, but wait for a pause so every keystroke is not a query.
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [q, type, geo]);

  const downloadTemplate = async () => {
    try {
      const r = await vendorTemplate();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data); a.download = 'vendor-template.xlsx'; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) { setError(apiError(e, 'Could not download the template')); }
  };

  return (
    <ReportShell icon="users" tint="#30B0C7" title="Vendor / Dealer"
      subtitle={loading && !rows.length ? 'Loading…' : `${total.toLocaleString('en-IN')} dealers on file`}
      note="Dealer Code here is what IFMS receives as the destination dealer. Keep this list in step with the company's master sheet."
      filters={
        <>
          <input style={S.search} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, code or district…" />
          <select style={S.select} value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">All types</option>
            {TYPES.map((t) => <option key={t} value={t}>{t[0] + t.slice(1).toLowerCase()}</option>)}
          </select>
          <select style={S.select} value={geo} onChange={(e) => setGeo(e.target.value)} aria-label="Map location">
            <option value="">Any location</option>
            <option value="PROBLEM">Needs attention</option>
            {Object.entries(GEO).map(([k, g]) => <option key={k} value={k}>{g.label}</option>)}
          </select>
          {canImport && <button type="button" style={P.btnPrimary} onClick={() => setAddOpen(true)}>＋ Add Dealer</button>}
          {canImport && <button type="button" style={P.btnGhost} onClick={() => setImportOpen(true)}>⬆ Import Excel</button>}
          {canImport && <button type="button" style={P.btnGhost} onClick={downloadTemplate}>Template</button>}
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--fx-text-3)' }}>
            {loading ? 'Searching…' : `${total.toLocaleString('en-IN')} match${total === 1 ? '' : 'es'}`}
          </span>
        </>
      }>

      <div style={P.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ ...P.table, minWidth: 820 }}>
            <thead>
              <tr>{['#', 'Dealer Code', 'Name', 'Type', 'State / District', 'Map Location', 'Mobile'].map((h) => <th key={h} style={P.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading && !rows.length && <tr><td colSpan={7} style={P.emptyTd}>Loading…</td></tr>}
              {!loading && error && <tr><td colSpan={7} style={{ ...P.emptyTd, color: 'var(--fx-red)' }}>{error}</td></tr>}
              {rows.map((v, i) => (
                <tr key={v.id}>
                  <td style={{ ...P.td, color: 'var(--fx-text-3)' }}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                  <td style={{ ...P.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{v.code}</td>
                  <td style={P.td}>{v.name}</td>
                  <td style={P.td}><Tag text={v.type[0] + v.type.slice(1).toLowerCase()} tone={TYPE_TONE[v.type] || 'gray'} /></td>
                  <td style={{ ...P.td, whiteSpace: 'nowrap' }}>
                    {v.districtName || v.districtCode || '—'}
                    <div style={P.muted}>{v.stateName || v.stateCode || ''}{v.districtCode ? ` · ${v.stateCode || '?'}/${v.districtCode}` : ''}</div>
                  </td>
                  <td style={P.td}>
                    {v.geoStatus
                      ? <span title={v.geoNote || ''}><Tag text={GEO[v.geoStatus]?.label || v.geoStatus} tone={GEO[v.geoStatus]?.tone || 'gray'} /></span>
                      : <span style={{ color: 'var(--fx-text-3)' }}>{v.latitude ? 'Not checked' : 'No location'}</span>}
                  </td>
                  <td style={P.td}>{v.mobile || '—'}</td>
                </tr>
              ))}
              {!loading && !error && rows.length === 0 && (
                <tr><td colSpan={7} style={P.emptyTd}>{q || type ? 'No dealer matches this search' : 'No dealers yet — import the Excel sheet'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div style={S.foot}>
          <span style={{ fontSize: 12, color: 'var(--fx-text-2)' }}>
            {total ? `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total.toLocaleString('en-IN')}` : 'No entries'}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button type="button" style={S.pg} disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</button>
            <span style={{ fontSize: 12, color: 'var(--fx-text-2)' }}>Page {page} / {pages}</span>
            <button type="button" style={S.pg} disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>›</button>
          </div>
        </div>
      </div>

      {addOpen && (
        <AddDealerModal
          onClose={() => setAddOpen(false)}
          onSaved={(code) => { setAddOpen(false); setQ(code); setPage(1); }}
        />
      )}

      {importOpen && (
        <ImportModal onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); setPage(1); load(); }} />
      )}
    </ReportShell>
  );
}

// ── Add one dealer ────────────────────────────────────────────────────────────
//
// The Excel import handles the master sheet; this is for the single dealer that
// arrives after it — a new retailer, or one the sheet missed. Same backend route,
// so the row goes through exactly the same LGD resolution the import does.
//
// State and district are DROPDOWNS fed by the government list, never free text.
// That is the whole point: IFMS validates destinationStateCode and
// destinationDistrictCode against this list, and a typed district is how a trip
// ends up rejected a day later. Picking the state first narrows the districts, so
// a Telangana dealer cannot be given an Andhra Pradesh district by accident.
// Every field on the add form, with the label the error message uses.
const REQUIRED: [keyof AddDealerForm, string][] = [
  ['code', 'Dealer Code'], ['name', 'Dealer Name'], ['agencyName', 'Agency Name'],
  ['type', 'Type'], ['mobile', 'Mobile'],
  ['stateCode', 'State'], ['districtCode', 'District'],
  ['latitude', 'Latitude'], ['longitude', 'Longitude'],
];
type AddDealerForm = {
  code: string; name: string; agencyName: string; type: string; stateCode: string;
  districtCode: string; mobile: string; latitude: string; longitude: string;
};

function AddDealerModal({ onClose, onSaved }: { onClose: () => void; onSaved: (code: string) => void }) {
  const { states, districts, loading: lgdLoading, error: lgdError } = useLgd();
  const [f, setF] = useState<AddDealerForm>({
    code: '', name: '', agencyName: '', type: 'RETAILER', stateCode: '', districtCode: '',
    mobile: '', latitude: '', longitude: '',
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((p) => ({ ...p, [k]: e.target.value, ...(k === 'stateCode' ? { districtCode: '' } : {}) }));

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); setErr('');
    // Every field is required. A dealer added here becomes a destination IFMS is
    // told about and a pin the truck is tracked to, so a half-filled row is worse
    // than no row: the trip is raised and then cannot be reported or tracked.
    const blank = REQUIRED.filter(([k]) => !String(f[k]).trim()).map(([, label]) => label);
    if (blank.length) {
      setErr(`${blank.join(', ')} ${blank.length > 1 ? 'are' : 'is'} required.`);
      return;
    }
    if (!/^\d{10}$/.test(f.mobile.trim())) { setErr('Mobile must be exactly 10 digits.'); return; }
    const la = Number(f.latitude), ln = Number(f.longitude);
    if (!Number.isFinite(la) || !Number.isFinite(ln) || la < 6.5 || la > 37.5 || ln < 68 || ln > 97.5) {
      setErr('Those coordinates are not inside India. Check latitude and longitude.'); return;
    }
    setSaving(true);
    try {
      // Names are sent alongside the codes so the dealer reads properly in the
      // list even before anyone runs the geo verification pass.
      const st = states.find((s) => s.code === f.stateCode);
      const di = districts.find((d) => d.code === f.districtCode);
      await saveVendor({
        code: f.code.trim(), name: f.name.trim(), agencyName: f.agencyName.trim(), type: f.type,
        stateCode: f.stateCode, stateName: st?.name || null,
        districtCode: f.districtCode, districtName: di?.name || null,
        mobile: f.mobile.trim(),
        latitude: la, longitude: ln,
      });
      onSaved(f.code.trim());
    } catch (e2) {
      setErr(apiError(e2, 'Could not save the dealer'));
    } finally { setSaving(false); }
  };

  const dists = districtOptions(districts, f.stateCode);

  return (
    <Modal title="Add dealer" subtitle="One dealer — the Excel import is for the whole sheet"
      onClose={onClose} width={640} footer={false}>
      <form onSubmit={submit}>
        <div style={S.formGrid}>
          <Field label="Dealer Code" req hint="What IFMS receives as the destination dealer">
            <input style={S.input} value={f.code} onChange={set('code')} placeholder="e.g. 1642243" autoFocus />
          </Field>
          <Field label="Dealer Name" req>
            <input style={S.input} value={f.name} onChange={set('name')} placeholder="e.g. Sri Sai Traders" />
          </Field>
          {/* The licence is held by the agency; the dealer name is the shop above the
              door. The import sheet carries both, so the form asks for both. */}
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Agency Name" req hint="The firm on the fertilizer licence, if it differs from the shop name">
              <input style={S.input} value={f.agencyName} onChange={set('agencyName')} placeholder="e.g. Sri Sai Agro Agencies" />
            </Field>
          </div>
          <Field label="Type" req>
            <select style={S.input} value={f.type} onChange={set('type')}>
              {TYPES.map((x) => <option key={x} value={x}>{x[0] + x.slice(1).toLowerCase()}</option>)}
            </select>
          </Field>
          <Field label="Mobile" req>
            <input style={S.input} value={f.mobile} onChange={set('mobile')} placeholder="10 digits" inputMode="numeric" />
          </Field>

          <Field label="State" req hint="From the government LGD list">
            <select style={S.input} value={f.stateCode} onChange={set('stateCode')} disabled={lgdLoading}>
              <option value="">{lgdLoading ? 'Loading…' : 'Select a state'}</option>
              {stateOptions(states).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="District" req hint={f.stateCode ? `${dists.length} districts in this state` : 'Pick a state first'}>
            <select style={S.input} value={f.districtCode} onChange={set('districtCode')} disabled={!f.stateCode}>
              <option value="">{f.stateCode ? 'Select a district' : '—'}</option>
              {dists.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>

          <Field label="Latitude" req hint="Where the truck must reach to count as delivered">
            <input style={S.input} value={f.latitude} onChange={set('latitude')} placeholder="e.g. 18.7604" inputMode="decimal" />
          </Field>
          <Field label="Longitude" req hint="Right-click the shop in Google Maps to copy both">
            <input style={S.input} value={f.longitude} onChange={set('longitude')} placeholder="e.g. 79.5189" inputMode="decimal" />
          </Field>
        </div>

        {lgdError && <div style={S.formErr}>{lgdError}</div>}
        {err && <div style={S.formErr}>{err}</div>}

        <div style={S.note2}>
          The coordinates are the pin the truck is tracked to — arrival, route deviation and the
          distance reported to IFMS are all measured from them, so a wrong pin makes a delivered
          load look undelivered. To find them, right-click the shop in Google Maps and copy the
          two numbers it shows.
        </div>

        <div style={S.actions}>
          <span style={{ flex: 1 }} />
          <button type="button" style={P.btnGhost} onClick={onClose}>Cancel</button>
          <button type="submit" style={{ ...P.btnPrimary, opacity: saving ? 0.7 : 1 }} disabled={saving}>
            {saving ? 'Saving…' : 'Save dealer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** One labelled field in the add form. */
function Field({ label, req, hint, children }: {
  label: string; req?: boolean; hint?: string; children: React.ReactNode;
}) {
  return (
    <div>
      <label style={S.fLabel}>{label}{req && <span style={{ color: 'var(--fx-red)' }}> *</span>}</label>
      {children}
      {hint && <div style={S.fHint}>{hint}</div>}
    </div>
  );
}

// ── Excel import: check the file first, then save ─────────────────────────────
function ImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [checked, setChecked] = useState<VendorImportResult | null>(null);
  const [done, setDone] = useState<VendorImportResult | null>(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const pick = (f: File | null) => { setFile(f); setChecked(null); setDone(null); setErr(''); };

  const run = async (dryRun: boolean) => {
    if (!file) return;
    setBusy(dryRun ? 'check' : 'import'); setErr('');
    try {
      const r = await importVendors(file, dryRun);
      if (dryRun) setChecked(r.data); else setDone(r.data);
    } catch (e) { setErr(apiError(e, 'Could not read the file')); }
    finally { setBusy(''); }
  };

  const summary = done || checked;
  return (
    <Modal title="Import dealer list" subtitle="Excel (.xlsx / .xls) — the first sheet is read" onClose={onClose} width={620} footer={false}>
      <label style={S.drop}>
        <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
          onChange={(e) => pick(e.target.files?.[0] || null)} />
        <span style={{ fontSize: 26 }}>📄</span>
        <b style={{ fontSize: 13.5, color: 'var(--fx-text)' }}>{file ? file.name : 'Choose an Excel file'}</b>
        <span style={{ fontSize: 12, color: 'var(--fx-text-2)' }}>
          {file
            ? `${(file.size / 1024 / 1024).toFixed(1)} MB — click to choose another`
            : 'A dealer code column and a name column are needed. Title rows above the headings are skipped, and any other column is kept with the dealer.'}
        </span>
      </label>

      {summary && (
        <div style={S.result}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 8 }}>
            <Stat label="Rows in file" value={summary.fileRows.toLocaleString('en-IN')} />
            <Stat label="New" value={summary.created.toLocaleString('en-IN')} tone="var(--fx-green)" />
            <Stat label="Updated" value={summary.updated.toLocaleString('en-IN')} tone="var(--fx-accent)" />
            <Stat label="Skipped" value={summary.skipped.toLocaleString('en-IN')} tone={summary.skipped ? 'var(--fx-orange)' : undefined} />
            {done && <Stat label="Total on file" value={summary.total.toLocaleString('en-IN')} />}
          </div>
          <div style={{ fontSize: 12, color: 'var(--fx-text-2)', lineHeight: 1.6 }}>
            Headings found on sheet row <b>{summary.headerRow}</b>.<br />
            {Object.entries(summary.columns).map(([field, heading]) => (
              <span key={field} style={S.mapChip}>{heading} → {field}</span>
            ))}
            {summary.ignoredColumns.length > 0 && (
              <div style={{ marginTop: 6 }}>Also kept with each dealer: {summary.ignoredColumns.join(', ')}</div>
            )}
          </div>
          {summary.problems.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12, color: 'var(--fx-orange)' }}>
              {summary.problems.map((p) => <li key={p}>{p}</li>)}
              {summary.skipped > summary.problems.length && <li>…and {summary.skipped - summary.problems.length} more</li>}
            </ul>
          )}
          {done && <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: 'var(--fx-green)' }}>Import finished.</div>}
        </div>
      )}
      {err && <div style={{ color: 'var(--fx-red)', fontSize: 12.5, marginTop: 10 }}>{err}</div>}

      <div style={S.actions}>
        <span style={{ flex: 1, fontSize: 11.5, color: 'var(--fx-text-3)' }}>
          {checked && !done ? 'Nothing has been saved yet — press Import to write these rows.' : 'A dealer already on file is corrected, not duplicated.'}
        </span>
        <button type="button" style={P.btnGhost} onClick={onClose}>{done ? 'Close' : 'Cancel'}</button>
        {!done && (
          <>
            <button type="button" style={{ ...P.btnGhost, opacity: !file || busy ? 0.6 : 1 }} disabled={!file || !!busy} onClick={() => run(true)}>
              {busy === 'check' ? 'Checking…' : 'Check file'}
            </button>
            <button type="button" style={{ ...P.btnPrimary, opacity: !file || busy ? 0.6 : 1 }} disabled={!file || !!busy} onClick={() => run(false)}>
              {busy === 'import' ? 'Importing…' : 'Import'}
            </button>
          </>
        )}
        {done && <button type="button" style={P.btnPrimary} onClick={onDone}>Show the list</button>}
      </div>
    </Modal>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div>
      <div style={{ fontSize: 17, fontWeight: 700, color: tone || 'var(--fx-text)' }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--fx-text-2)' }}>{label}</div>
    </div>
  );
}

const S = css({
  search: { padding: '7px 11px', borderRadius: 8, border: '1px solid var(--fx-border-strong)', background: 'var(--fx-input)', color: 'var(--fx-text)', fontSize: 12.5, outline: 'none', minWidth: 240, fontFamily: 'inherit' },
  select: { padding: '6px 9px', borderRadius: 8, border: '1px solid var(--fx-border-strong)', fontSize: 12, background: 'var(--fx-input)', color: 'var(--fx-text)', fontFamily: 'inherit' },
  addr: { display: 'inline-block', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' },
  foot: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 14px', borderTop: '1px solid var(--fx-border)' },
  pg: { minWidth: 30, height: 28, borderRadius: 7, border: '1px solid var(--fx-border-strong)', background: 'var(--fx-surface)', color: 'var(--fx-text)', fontSize: 13, cursor: 'pointer' },
  drop: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '26px 18px', border: '2px dashed var(--fx-border-strong)', borderRadius: 14, cursor: 'pointer', textAlign: 'center', background: 'var(--fx-surface-2)' },
  mapChip: { display: 'inline-block', background: 'var(--fx-gray-soft)', borderRadius: 6, padding: '2px 7px', margin: '3px 5px 0 0', fontSize: 11, color: 'var(--fx-text)' },
  result: { marginTop: 14, padding: '12px 14px', borderRadius: 12, border: '1px solid var(--fx-border)', background: 'var(--fx-surface)' },
  actions: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--fx-border)' },

  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 14 },
  fLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--fx-text-2)', marginBottom: 5 },
  fHint: { fontSize: 11, color: 'var(--fx-text-3)', marginTop: 4, lineHeight: 1.45 },
  input: { width: '100%', boxSizing: 'border-box', padding: '8px 11px', borderRadius: 9, border: '1px solid var(--fx-border-strong)', background: 'var(--fx-input)', color: 'var(--fx-text)', fontSize: 13, outline: 'none', fontFamily: 'inherit' },
  formErr: { marginTop: 13, color: 'var(--fx-red)', background: 'var(--fx-red-soft)', borderRadius: 9, fontSize: 12.5, padding: '9px 12px', lineHeight: 1.5 },
  note2: { marginTop: 13, fontSize: 11.5, color: 'var(--fx-text-2)', background: 'var(--fx-gray-soft)', border: '1px solid var(--fx-border)', borderRadius: 9, padding: '9px 12px', lineHeight: 1.55 },
});
